import { useEffect, useRef, useState } from "react";
import { ShieldCheck, MapPin, ArrowRight } from "lucide-react";
import { fontSans, fontMono, color as T } from "../design/tokens";

// Darius Match Card — compact photo preview + attached CTA strip.
//
// The photo and the CTA strip are one interactive object: the whole card
// links to /match-example (opens in a new window). The strip carries two
// lines of copy — a curiosity headline and a plain-English description
// of what will open.

const DARIUS_PHOTO =
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1400&q=80";

export default function DariusMatchCard() {
  const rootRef = useRef(null);
  const [cardIn, setCardIn] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setCardIn(true);
      return;
    }
    const node = rootRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setCardIn(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      className="w-full mx-auto"
      style={{ maxWidth: 400 }}
      data-testid="darius-match-card-wrapper"
    >
      <a
        href="/match-example"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="darius-match-card"
        aria-label="See why this is an 84% match — open the sample HAEVN match report in a new window"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="relative block w-full"
        style={{
          background: "#FFFFFF",
          border: `1px solid ${hover ? "rgba(15,118,110,0.45)" : T.line}`,
          borderRadius: 14,
          overflow: "hidden",
          textDecoration: "none",
          color: "inherit",
          transform: cardIn ? "translateY(0)" : "translateY(14px)",
          opacity: cardIn ? 1 : 0,
          transition:
            "transform 700ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms ease, border-color 220ms ease, box-shadow 220ms ease",
          boxShadow: hover
            ? "0 1px 0 rgba(11,18,32,0.05), 0 34px 70px -34px rgba(11,18,32,0.30)"
            : "0 1px 0 rgba(11,18,32,0.04), 0 22px 50px -30px rgba(11,18,32,0.16)",
        }}
      >
        {/* Photograph + identity overlay */}
        <div className="relative" style={{ aspectRatio: "16 / 10", background: "#0B1220" }}>
          <img
            src={DARIUS_PHOTO}
            alt="Darius, 35 — Austin"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "50% 22%" }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.78) 100%)",
            }}
          />

          {/* Distance pill — top left */}
          <div
            className="absolute top-3 left-3"
            data-testid="darius-distance"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 9px",
              borderRadius: 5,
              background: "rgba(5,8,16,0.6)",
              backdropFilter: "blur(4px)",
              color: "#FFFFFF",
              fontFamily: fontSans,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <MapPin className="w-3 h-3" strokeWidth={2} />
            4 miles away
          </div>

          {/* 84% match chip — top right, does not obscure the face */}
          <div
            className="absolute top-3 right-3"
            data-testid="darius-match-chip"
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 6,
              padding: "6px 10px",
              borderRadius: 999,
              background: T.accent,
              color: "#FFFFFF",
              fontFamily: fontSans,
              boxShadow: "0 6px 20px -8px rgba(15,118,110,0.55)",
            }}
          >
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                lineHeight: 1,
              }}
            >
              84%
            </span>
            <span
              style={{
                fontFamily: fontMono,
                fontSize: 9.5,
                letterSpacing: "0.18em",
                fontWeight: 600,
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.92)",
              }}
            >
              Match
            </span>
          </div>

          {/* Identity block — bottom overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="flex items-center gap-2">
              <span
                data-testid="darius-name"
                style={{
                  fontFamily: fontSans,
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  lineHeight: 1,
                  color: "#FFFFFF",
                }}
              >
                Darius, 35
              </span>
              <span
                aria-label="Verified"
                data-testid="darius-verified"
                className="inline-flex items-center justify-center"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  background: T.accent,
                  color: "#FFFFFF",
                }}
              >
                <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2.5} />
              </span>
            </div>
            <div
              className="mt-1.5"
              style={{
                fontFamily: fontSans,
                fontSize: 13,
                color: "rgba(255,255,255,0.92)",
                lineHeight: 1.4,
              }}
            >
              He / Him&nbsp;·&nbsp;Straight&nbsp;·&nbsp;Monogamous
            </div>
            <div
              style={{
                fontFamily: fontSans,
                fontSize: 13,
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.4,
              }}
            >
              Austin, TX
            </div>
          </div>
        </div>

        {/* Attached CTA strip — pale teal, thin top border, teal text */}
        <div
          data-testid="darius-explore-cta"
          className="flex items-center justify-between gap-4"
          style={{
            padding: "16px 18px",
            background: hover ? "rgba(15,118,110,0.08)" : "rgba(15,118,110,0.04)",
            borderTop: `1px solid ${
              hover ? "rgba(15,118,110,0.30)" : "rgba(15,118,110,0.18)"
            }`,
            transition: "background-color 220ms ease, border-color 220ms ease",
          }}
        >
          <div className="min-w-0">
            <div
              style={{
                fontFamily: fontSans,
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: "-0.005em",
                color: T.accent,
                lineHeight: 1.2,
              }}
            >
              See why this is an 84% match
            </div>
            <div
              className="mt-1"
              style={{
                fontFamily: fontSans,
                fontSize: 12.5,
                fontWeight: 400,
                color: "rgba(15,118,110,0.78)",
                lineHeight: 1.35,
              }}
            >
              Open the sample HAEVN match report
            </div>
          </div>
          <ArrowRight
            className="w-5 h-5 flex-shrink-0"
            strokeWidth={2}
            style={{
              color: T.accent,
              transform: hover ? "translateX(4px)" : "translateX(0)",
              transition: "transform 220ms ease",
              animation: hover ? "none" : "dariusCtaNudge 1.8s ease-in-out infinite",
            }}
          />
        </div>
      </a>
      {/* Attention-grabbing "come this way" nudge for the CTA arrow.
          Pauses on hover so the direct arrow-shift feedback stays crisp. */}
      <style>{`
        @keyframes dariusCtaNudge {
          0%, 55%, 100% { transform: translateX(0); }
          15% { transform: translateX(6px); }
          30% { transform: translateX(0); }
          40% { transform: translateX(4px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="darius-match-card"] svg { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
